import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { usePlaybackState } from 'hooks/usePlaybackState';
import type {
  TerrierCadence,
  TerrierEnableForRange,
  TerrierLayer,
  TerrierOverlay,
  TerrierSource,
  TerrierSourceConfig,
} from '../vendor/terrier';
import type { TerrierRadarConfig } from './useTerrierRadar';
import { createRadarColorMap } from './radarColorMap';
import { createManifestLoadHandler } from './handleManifestLoad';
import terrier from '../vendor/terrier';

const RADAR_MASK_LAYER_ID = 'radar-mask-layer';
const TERRIER_MAPLIBRE_LAYER_ID = 'terrier';

type SetupTerrierLayerOptions = {
  overlay: TerrierOverlay;
  config: TerrierRadarConfig;
  map: MapboxMap;
  timeDataRef: MutableRefObject<{
    min: number;
    max: number;
    frameCount: number;
  } | null>;
  overlayRef: MutableRefObject<TerrierOverlay | null>;
  layerRef: MutableRefObject<TerrierLayer | null>;
  setIsInitialized: Dispatch<SetStateAction<boolean>>;
  playbackState: ReturnType<typeof usePlaybackState>;
  playbackRef: MutableRefObject<ReturnType<typeof usePlaybackState>>;
};

const configureSource = (params: {
  config: TerrierSourceConfig;
  cadence: TerrierCadence;
  enableForRange: TerrierEnableForRange;
}): TerrierSource[] => {
  const { config, cadence, enableForRange } = params;
  const sources = terrier.sourcesForVariable(config);
  return sources.map((source) => ({ ...source, cadence, enableForRange }));
};

/**
 * Configures and starts a Terrier radar layer, updating all provided refs/state.
 */
export const setupTerrierLayer = ({
  overlay: terrierOverlay,
  config,
  map,
  timeDataRef,
  overlayRef,
  layerRef,
  setIsInitialized,
  playbackState,
  playbackRef,
}: SetupTerrierLayerOptions): void => {
  const allSources = config.sources.flatMap((source) =>
    configureSource(source),
  );
  const primaryVariable = config.sources[0].config.variable;

  // get the min / max and max frames cadence across all sources
  const cadences = config.sources.map((s) => s.cadence);
  const pastFrames = cadences
    .filter((c) => c[1] === 0)
    .reduce((max, c) => Math.max(max, c[2]), 0);
  const futureFrames = cadences
    .filter((c) => c[0] === 0)
    .reduce((max, c) => Math.max(max, c[2]), 0);
  const overallCadence: TerrierCadence = [
    Math.min(...cadences.map((c) => c[0])),
    Math.max(...cadences.map((c) => c[1])),
    pastFrames + futureFrames,
  ];

  const loadCallback = createManifestLoadHandler(
    terrierOverlay,
    () => playbackRef.current.isPlaying,
    overallCadence[1],
    {
      setTimeData: (min, max, frameCount) => {
        // eslint-disable-next-line no-param-reassign
        timeDataRef.current = { min, max, frameCount };
      },
      setFrames: playbackState.setFrames,
      jumpToFrame: playbackState.jumpToFrame,
    },
  );

  const layer = terrierOverlay.startLayer(primaryVariable, {
    sources: allSources,
    colorMap: createRadarColorMap(terrier),
    cadence: overallCadence,
    interpMode: 'linear' as const,
    opacity: config.opacity,
    importFactor: 16.0,
    loadCallback,
  });

  terrierOverlay.setNearestFrame(true);
  terrierOverlay.timePause();

  // Add dark mask layer below radar if configured
  if (config.maskOpacity && config.maskOpacity > 0) {
    if (map.getLayer(RADAR_MASK_LAYER_ID)) {
      map.removeLayer(RADAR_MASK_LAYER_ID);
    }
    map.addLayer(
      {
        id: RADAR_MASK_LAYER_ID,
        type: 'background',
        paint: {
          'background-color': '#000000',
          'background-opacity': config.maskOpacity,
        },
      },
      TERRIER_MAPLIBRE_LAYER_ID,
    );
  }

  /* eslint-disable no-param-reassign */
  overlayRef.current = terrierOverlay;
  layerRef.current = layer;
  /* eslint-enable no-param-reassign */
  setIsInitialized(true);
};

/**
 * Removes the radar mask layer if present.
 */
export const removeRadarMaskLayer = (map: MapboxMap | null): void => {
  if (map?.getLayer(RADAR_MASK_LAYER_ID)) {
    try {
      map.removeLayer(RADAR_MASK_LAYER_ID);
    } catch {
      // Ignore cleanup errors
    }
  }
};
